import _objectWithoutProperties from '@babel/runtime-corejs3/helpers/esm/objectWithoutProperties';
import _objectSpread from '@babel/runtime-corejs3/helpers/esm/objectSpread2';
import * as React from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getComponentName } from '@thisisbud/react-utils';
import { jsx } from 'react/jsx-runtime';

const _excluded = ["translationKey"];
const I18nContext = /*#__PURE__*/React.createContext(null); // eslint-disable-line @typescript-eslint/naming-convention

/**
 * Provides an i18n context that allows using the I18n class in React components.
 *
 * @param props - Provider props including the I18n instance to provide to consumers of the I18n context
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import I18n from '@thisisbud/i18n'
 * import { I18nProvider } from '@thisisbud/i18n-react'
 *
 * const i18n = new I18n('en-GB', {
 *   translations: {
 *     foobar: 'The quick brown fox jumps over the lazy dog',
 *   },
 * })
 *
 * function Foo(props) {
 *   return (
 *     <I18nProvider i18n={i18n}>
 *       {props.children}
 *     </I18nProvider>
 *   )
 * }
 * ```
 */

function I18nProvider(props) {
  const {
    children,
    i18n
  } = props;
  return /*#__PURE__*/jsx(I18nContext.Provider, {
    value: i18n,
    children: children
  });
}
I18nProvider.displayName = 'I18nProvider';
/**
 * Use the current I18n instance provided by the closest I18n context provider above.
 *
 * @returns The I18n instance currently provided
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { useI18n } from '@thisisbud/i18n-react'
 *
 * function Foobar() {
 *   const i18n = useI18n()
 *
 *   return (
 *     <button className="btn">
 *       {i18n.t('clickHere')}
 *     </button>
 *   )
 * }
 * ```
 */

function useI18n() {
  return React.useContext(I18nContext);
}
/**
 * Wraps a React component in a HOC which passes the current I18n instance as a prop.
 *
 * @remarks The current I18n instance will be available in the `i18n` prop
 * @param Component - A React component to receive the current I18n instance as a prop
 * @returns A HOC which will add the current I18n instance as a prop to the given React component
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { withI18n } from '@thisisbud/i18n-react'
 *
 * function Foobar(props) {
 *   const { i18n } = props
 *
 *   return (
 *     <button className="btn">
 *       {i18n.t('clickHere')}
 *     </button>
 *   )
 * }
 *
 * export default withI18n(Foobar)
 * ```
 */

function withI18n(Component) {
  const withForwardedRef = /*#__PURE__*/React.forwardRef( // eslint-disable-line react/no-multi-comp
  function (props, ref) {
    const i18n = useI18n();

    const combinedProps = _objectSpread(_objectSpread({}, props), {}, {
      i18n
    });

    return /*#__PURE__*/jsx(Component, _objectSpread({
      ref: ref
    }, combinedProps)); // eslint-disable-line react/jsx-props-no-spreading
  }); // @ts-expect-error exposing the wrapped component via the WrappedComponent property is a common pratice

  withForwardedRef.WrappedComponent = Component;
  withForwardedRef.displayName = `withI18n(${getComponentName(Component)})`;
  return hoistNonReactStatics(withForwardedRef, Component);
}
/**
 * A React component that allows to render a translation directly as part of other components.
 *
 * @remarks Like any React component, the translation will only re-render if the props change
 * @param props - The component props will be treated as the translation context
 * @returns The rendered output of the translation
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { T } from '@thisisbud/i18n-react'
 *
 * function Foobar() {
 *   return (
 *     <button className="btn">
 *       <T translationKey='clickHere'/>
 *     </button>
 *   )
 * }
 * ```
 */

function T(props) {
  const i18n = useI18n();

  const {
    translationKey
  } = props,
        ctx = _objectWithoutProperties(props, _excluded);

  return /*#__PURE__*/jsx(React.Fragment, {
    children: i18n.tToParts(translationKey, ctx)
  }); // eslint-disable-line react/jsx-no-useless-fragment
}

export { I18nProvider, T, useI18n, withI18n };
